import type { ApolloError, Operation } from '@apollo/client';
import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import type { ConstValues } from '@openx/types/helpers.ts';
import type { QueryClient } from '@tanstack/react-query';
import type { Auth } from 'firebase/auth';
import type { DocumentNode } from 'graphql';
import type { ReactNode } from 'react';

export interface GraphQLProviderProps {
  children: ReactNode;
}

type SingleGQLError = ApolloError['graphQLErrors'][0];

export type LocalErrorHandler = (error: NestedError) => boolean;

export type HandlersConfigMap = Map<string, LocalErrorHandler[]>;

export interface ParseGraphQLErrorsArgs {
  graphQLErrors: readonly SingleGQLError[];
  handlersConfigMap: HandlersConfigMap;
  operation: Operation;
}

export interface MakeApolloClientArgs {
  auth: Auth;
  handlersConfigMap: HandlersConfigMap;
  printError: (message: string) => void;
}

export const NestedErrorCode = {
  JOINT_UNIQUENESS_VIOLATION: 'joint-uniqueness-violation',
  SINGLE_UNIQUENESS_VIOLATION: 'single-uniqueness-violation',
  NOT_NULL_VIOLATION: 'not-null-violation',
  VALIDATION_ERROR: 'validation-error',
  VALUE_ERROR: 'value-error',
  RANGE_ERROR: 'range-error',
  NOT_UNIQUE_ERROR: 'not-unique-error',
  BAD_USER_INPUT: 'BAD_USER_INPUT',
} as const;
export type NestedErrorCode = ConstValues<typeof NestedErrorCode>;

export interface NestedError {
  code: NestedErrorCode;
  choices?: string[] | number[];
  max?: string | number;
  message: string;
  min?: string | number;
  path: string;
}

export type ExtendedGQLError = ApolloError['graphQLErrors'][0] & {
  extensions?: {
    code?: string;
    errors?: NestedError[];
    exception?: {
      stacktrace?: string[];
      message?: string;
    };
  };
};

type ConfigContextFunctionSignature = (document: DocumentNode, handler: LocalErrorHandler) => void;

export type GraphQLRequest = <TData, TVariables>(
  document: TypedDocumentNode<TData, TVariables>,
  variables?: TVariables
) => Promise<TData>;

export interface ConfigContextValue {
  registerLocalErrorHandler: ConfigContextFunctionSignature;
  unregisterLocalErrorHandler: ConfigContextFunctionSignature;
}

export const getGraphQLRequestFromClient = (queryClient: QueryClient): GraphQLRequest => {
  // query meta is typed app-wide in shared/types/react-query.d.ts; graphQLRequest is OA-specific, so narrow it here
  const graphQLRequest = queryClient.getDefaultOptions().queries?.meta?.['graphQLRequest'] as
    | GraphQLRequest
    | undefined;

  if (!graphQLRequest) {
    throw new Error('graphQLRequest is not configured on the QueryClient');
  }

  return graphQLRequest;
};

export const getFederatedGraphQLRequestFromClient = (queryClient: QueryClient): GraphQLRequest => {
  const federatedGraphQLRequest = queryClient.getDefaultOptions().queries?.meta?.['federatedGraphQLRequest'] as
    | GraphQLRequest
    | undefined;

  if (!federatedGraphQLRequest) {
    throw new Error('federatedGraphQLRequest is not configured on the QueryClient');
  }

  return federatedGraphQLRequest;
};
